import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const Layout = ({ nav, children, headerMode = 'fixed' }) => {
  const isFixed = headerMode === 'fixed';
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!isFixed) return;
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFixed]);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Header */}
      <header
        className={
          isFixed
            ? `fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                  ? 'bg-white/90 backdrop-blur-2xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] border-b border-surface-100'
                  : 'bg-transparent'
              }`
            : 'sticky top-0 z-50 bg-white border-b border-surface-100 shadow-sm'
        }
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="relative w-9 h-9">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-500 to-pink-500 rounded-xl rotate-0 group-hover:rotate-6 transition-transform duration-300" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-bold text-sm tracking-wide">T</span>
                </div>
              </div>
              <span className={`text-xl font-bold font-tilt transition-colors duration-300 ${isFixed && !scrolled ? 'text-white' : 'text-surface-900'}`}>
                Tixy<span className="text-accent-500">.az</span>
              </span>
            </Link>
            <nav className="flex items-center gap-2">
              {nav}
              <Link
                to="/about"
                className={`text-sm font-medium px-4 py-2 rounded-full transition-all duration-300 ${
                  isFixed && !scrolled
                    ? 'text-white/70 hover:text-white hover:bg-white/10'
                    : 'text-surface-600 hover:text-surface-900 hover:bg-surface-100'
                }`}
              >
                Haqqımızda
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative bg-surface-950 text-surface-400 overflow-hidden">
        {/* Subtle gradient accent on top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/50 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
            {/* Brand col */}
            <div className="md:col-span-5">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xs">T</span>
                </div>
                <span className="text-lg font-bold text-white font-tilt">
                  Tixy<span className="text-accent-400">.az</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed text-surface-500 max-w-sm">
                Azərbaycanın premium bilet platforması. Konsertlər, festivallar və xüsusi tədbirlərə biletinizi rahat və təhlükəsiz əldə edin.
              </p>
            </div>

            {/* Links */}
            <div className="md:col-span-3">
              <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-4">Naviqasiya</h4>
              <div className="flex flex-col gap-3">
                <Link to="/" className="text-sm text-surface-400 hover:text-white transition-colors duration-200">Ana Səhifə</Link>
                <Link to="/about" className="text-sm text-surface-400 hover:text-white transition-colors duration-200">Haqqımızda</Link>
                <Link to="/organizer" className="text-sm text-surface-400 hover:text-white transition-colors duration-200">Tədbirinizi satın</Link>
                <Link to="/privacy-policy" className="text-sm text-surface-400 hover:text-white transition-colors duration-200">Məxfilik Siyasəti</Link>
              </div>
            </div>

            {/* Contact */}
            <div className="md:col-span-4">
              <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-4">Əlaqə</h4>
              <div className="flex flex-col gap-3">
                <a href="tel:+994556611161" className="text-sm text-surface-400 hover:text-white transition-colors duration-200 inline-flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-surface-800 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-accent-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                  </div>
                  +994 55 661 11 61
                </a>
                <a href="mailto:info@tixy.az" className="text-sm text-surface-400 hover:text-white transition-colors duration-200 inline-flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-surface-800 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-accent-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  info@tixy.az
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-surface-800/60 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xs text-surface-600">
              &copy; {new Date().getFullYear()} Tixy.az &mdash; Bütün hüquqlar qorunur.
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-1.5 text-xs text-surface-500">
                <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                </svg>
                256-bit SSL
              </div>
              <div className="flex items-center gap-1.5 text-xs text-surface-500">
                <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                PCI DSS
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
